import { Link, useNavigate } from 'react-router-dom'
import {
  Building2,
  Car,
  Wrench,
  Crown,
  Zap,
  CheckCircle2,
  Lock,
  ShieldCheck,
  Sparkles,
  PackageOpen,
  Plus,
  ArrowRight,
  FileEdit,
  Clock,
  Edit,
} from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useAuth } from '@/providers/auth-provider'
import {
  useDealerDashboard,
  useDealerPermissions,
  useDealerSubscription,
} from '../../hooks/use-dealer'
import { useMyCarListings, usePlatformUsage } from '@/modules/cars/hooks/use-cars'
import { useMyOrders } from '@/modules/cart/hooks/use-cart'
import { appRoutes } from '@/constants'

export function DealerDashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: dashboard, isLoading: loadingDash } = useDealerDashboard()
  const { data: subscription } = useDealerSubscription()
  const { data: permissions } = useDealerPermissions()
  const { data: listings, isLoading: loadingListings } = useMyCarListings()
  const { data: usage } = usePlatformUsage()
  const { data: myOrders = [] } = useMyOrders()

  const allListings = listings || []

  const carStats = {
    total: allListings.length,
    published: allListings.filter((c) => c.status === 'PUBLISHED').length,
    pending: allListings.filter((c) => c.status === 'PENDING_REVIEW').length,
    drafts: allListings.filter((c) => c.status === 'DRAFT').length,
    rejected: allListings.filter((c) => c.status === 'REJECTED').length,
  }

  const isSubActive =
    subscription &&
    (subscription.status === 'ACTIVE' || subscription.status === 'TRIAL' || subscription.status === 'RENEWED')

  const maxCars = usage?.maxCars ?? 25
  const remainingCars = usage?.remainingCars ?? 0
  const carsUsed = Math.max(0, maxCars - remainingCars)

  const recentVehicles = allListings.slice(0, 5)

  const permissionItems = [
    { label: 'Create Car Listings', allowed: permissions?.canCreateCar?.allowed },
    { label: 'Create Parts Listings', allowed: permissions?.canCreatePart?.allowed },
    { label: 'Boost Listings', allowed: permissions?.canBoostListing?.allowed },
    { label: 'Purchase Leads', allowed: permissions?.canPurchaseLead?.allowed },
    { label: 'Access Analytics', allowed: permissions?.canAccessAnalytics?.allowed },
    { label: 'Featured Listings', allowed: permissions?.canUseFeaturedListings?.allowed },
    { label: 'Priority Support', allowed: permissions?.hasPrioritySupport?.allowed },
  ]

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <SectionHeading
              eyebrow="Enterprise Dealership Portal"
              title="Dealership Operations Center"
              description={`Welcome back, ${user?.firstName ?? 'Dealership'}. Manage enterprise vehicle fleet, stock quotas, and leads.`}
              align="left"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={() => navigate('/dealer/cars')}>
            <Building2 className="size-4 mr-2" /> Showroom Inventory
          </Button>
          <Button onClick={() => navigate('/dealer/cars/new')} className="gap-2 font-bold shadow-glow bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="size-4" /> + Add Vehicle
          </Button>
        </div>
      </div>

      {/* Subscription Action Guard Banner */}
      {!isSubActive && (
        <Card className="border-purple-500/40 bg-gradient-to-r from-purple-500/10 via-card to-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <span className="text-xs font-bold text-purple-400 uppercase flex items-center gap-1.5">
                <Crown className="size-4" /> Dealership Subscription Required
              </span>
              <h3 className="font-display text-lg font-bold text-foreground">
                Activate Your Enterprise Dealership Membership
              </h3>
              <p className="text-xs text-muted-foreground">
                Activate your business subscription to unlock fleet vehicle listings, buyer leads, and featured showroom options.
              </p>
            </div>
            <Button onClick={() => navigate('/dealer/billing')} className="gap-2 font-bold bg-purple-600 text-white shadow-glow shrink-0">
              <Zap className="size-4" /> Select Subscription Plan
            </Button>
          </div>
        </Card>
      )}

      {/* DEALERSHIP VEHICLE FLEET KPI SUMMARY */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-500">
              <Building2 className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-medium">Total Fleet</p>
              <p className="font-display text-2xl font-bold text-foreground">
                {loadingListings ? '...' : carStats.total}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
              <CheckCircle2 className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-medium">Published Fleet</p>
              <p className="font-display text-2xl font-bold text-emerald-500">
                {loadingListings ? '...' : carStats.published}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
              <Clock className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-medium">Pending Review</p>
              <p className="font-display text-2xl font-bold text-amber-500">
                {loadingListings ? '...' : carStats.pending}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-foreground">
              <FileEdit className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-medium">Draft Stock</p>
              <p className="font-display text-2xl font-bold text-foreground">
                {loadingListings ? '...' : carStats.drafts}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-400">
              <Crown className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-medium">Remaining Capacity</p>
              <p className="font-display text-2xl font-bold text-purple-400">
                {remainingCars}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* QUICK ACTIONS & FLEET CAPACITY MONITOR */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Quick Actions Panel */}
        <Card className="md:col-span-1 border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-display text-base font-bold text-foreground flex items-center gap-2">
              <Zap className="size-4 text-purple-400" /> Dealership Quick Actions
            </h3>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/dealer/cars/new')}
                className="w-full justify-start gap-2 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="size-4" /> Add Vehicle to Fleet
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dealer/cars')}
                className="w-full justify-start gap-2 text-xs font-semibold"
              >
                <Building2 className="size-4" /> Manage Fleet Inventory
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dealer/cars?status=DRAFT')}
                className="w-full justify-start gap-2 text-xs font-semibold"
              >
                <FileEdit className="size-4" /> Continue Draft Stock ({carStats.drafts})
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dealer/cars?status=PENDING_REVIEW')}
                className="w-full justify-start gap-2 text-xs font-semibold text-amber-500"
              >
                <Clock className="size-4" /> View Pending Approvals ({carStats.pending})
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fleet Quota Monitor */}
        <Card className="md:col-span-2 border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <div className="flex items-center gap-2">
                <Crown className="size-5 text-purple-400" />
                <h3 className="font-display text-base font-bold text-foreground">
                  Dealership Quota &amp; Capacity
                </h3>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate('/dealer/billing')} className="text-xs font-bold gap-1">
                Manage Subscription <ArrowRight className="size-3" />
              </Button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-muted-foreground">Plan: <strong className="text-foreground">{subscription?.planName || 'Dealer Business Plan'}</strong></span>
                <span className="text-foreground">{carsUsed} / {maxCars} Vehicles Listed ({remainingCars} Capacity Remaining)</span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-accent/60 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${carsUsed >= maxCars ? 'bg-amber-500' : 'bg-purple-500'}`}
                  style={{ width: `${Math.min(100, (carsUsed / (maxCars || 1)) * 100)}%` }}
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 text-xs">
                <div className="p-3 rounded-xl border border-border/60 bg-background/50 space-y-0.5">
                  <span className="text-muted-foreground text-[10px] uppercase font-bold block">Vehicle Allowance</span>
                  <span className="font-bold text-foreground">{remainingCars} remaining</span>
                </div>
                <div className="p-3 rounded-xl border border-border/60 bg-background/50 space-y-0.5">
                  <span className="text-muted-foreground text-[10px] uppercase font-bold block">Draft Limit</span>
                  <span className="font-bold text-foreground">{usage?.remainingDraftCars ?? 0} remaining</span>
                </div>
                <div className="p-3 rounded-xl border border-border/60 bg-background/50 space-y-0.5">
                  <span className="text-muted-foreground text-[10px] uppercase font-bold block">Boost Credits</span>
                  <span className="font-bold text-purple-400">{dashboard?.usage?.remainingBoostCredits ?? 0} credits</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* RECENT VEHICLES SECTION */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <div className="flex items-center gap-2">
              <Car className="size-5 text-purple-400" />
              <h3 className="font-display text-base font-bold text-foreground">Recent Showroom Vehicles</h3>
            </div>
            <Link to="/dealer/cars" className="text-xs font-bold text-purple-400 hover:underline flex items-center gap-1">
              View All Vehicles ({carStats.total}) <ArrowRight className="size-3" />
            </Link>
          </div>

          {recentVehicles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-accent/40 text-muted-foreground uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="p-3">Vehicle Details</th>
                    <th className="p-3">Asking Price</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Updated</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentVehicles.map((car: any) => (
                    <tr key={car.id} className="hover:bg-accent/20 transition-colors">
                      <td className="p-3 font-bold">
                        <div className="flex items-center gap-3">
                          <div className="size-10 rounded-lg overflow-hidden border border-border shrink-0 bg-accent/20">
                            {Array.isArray(car.images) && car.images[0] ? (
                              <img src={typeof car.images[0] === 'string' ? car.images[0] : car.images[0].url} alt="" className="size-full object-cover" />
                            ) : (
                              <Car className="size-5 m-2.5 text-purple-400" />
                            )}
                          </div>
                          <div>
                            <div className="line-clamp-1">{car.title}</div>
                            <div className="text-xs font-normal text-muted-foreground">{car.manufactureYear} • {car.fuelType} • {car.locationCity}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 font-bold">₹{Number(car.askingPrice).toLocaleString('en-IN')}</td>
                      <td className="p-3">
                        <Badge
                          className={
                            car.status === 'PUBLISHED'
                              ? 'bg-emerald-500/20 text-emerald-500'
                              : car.status === 'PENDING_REVIEW'
                              ? 'bg-amber-500/20 text-amber-500'
                              : car.status === 'REJECTED'
                              ? 'bg-rose-500/20 text-rose-500'
                              : 'bg-muted text-muted-foreground'
                          }
                        >
                          {car.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(car.updatedAt || car.createdAt).toLocaleDateString()}</td>
                      <td className="p-3 text-right">
                        <Link to={`/dealer/cars/${car.id}/edit`}>
                          <Button size="sm" variant="ghost" className="size-8 p-0">
                            <Edit className="size-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center space-y-3">
              <PackageOpen className="mx-auto size-10 text-muted-foreground opacity-50" />
              <p className="text-sm font-bold text-foreground">No Showroom Vehicles Listed Yet</p>
              <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                Add vehicles to your dealership inventory to reach thousands of prospective car buyers.
              </p>
              <Button onClick={() => navigate('/dealer/cars/new')} className="gap-2 font-bold text-xs bg-purple-600 hover:bg-purple-700 text-white shadow-glow">
                + Add First Vehicle
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PLACED PARTS ORDERS SECTION */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <div className="flex items-center gap-2">
              <PackageOpen className="size-5 text-purple-400" />
              <h3 className="font-display text-base font-bold text-foreground">My Placed Parts Orders</h3>
              {myOrders.length > 0 && (
                <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                  {myOrders.length} placed
                </Badge>
              )}
            </div>
            <Link to="/dealer/orders" className="text-xs font-bold text-purple-400 hover:underline flex items-center gap-1">
              View All Orders ({myOrders.length}) <ArrowRight className="size-3" />
            </Link>
          </div>

          {myOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-accent/40 text-muted-foreground uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="p-3">Order Number</th>
                    <th className="p-3">Items Purchased</th>
                    <th className="p-3">Total Amount</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Date</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {myOrders.slice(0, 5).map((order: any) => {
                    const status = (order.orderStatus || order.status || 'PROCESSING').toUpperCase()
                    return (
                      <tr key={order.id} className="hover:bg-accent/20 transition-colors">
                        <td className="p-3 font-mono font-bold text-xs text-foreground">
                          {order.orderNumber}
                        </td>
                        <td className="p-3">
                          <div className="text-xs font-semibold text-foreground line-clamp-1">
                            {order.items?.[0]?.title || 'Vehicle Spare Part'}
                          </div>
                          {order.items?.length > 1 && (
                            <div className="text-[10px] text-muted-foreground">
                              +{order.items.length - 1} more item(s)
                            </div>
                          )}
                        </td>
                        <td className="p-3 font-display font-bold text-foreground">
                          ₹{Number(order.totalAmount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="p-3">
                          <Badge
                            className={
                              status === 'DELIVERED'
                                ? 'bg-emerald-500/20 text-emerald-500 font-bold'
                                : status === 'SHIPPED'
                                ? 'bg-blue-500/20 text-blue-500 font-bold'
                                : status === 'CANCELLED'
                                ? 'bg-rose-500/20 text-rose-500 font-bold'
                                : 'bg-amber-500/20 text-amber-500 font-bold'
                            }
                          >
                            {status}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-right">
                          <Link to="/dealer/orders">
                            <Button size="sm" variant="outline" className="text-xs font-bold h-7 px-2.5">
                              Track Details
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center space-y-2 border border-dashed border-border/80 rounded-xl">
              <PackageOpen className="mx-auto size-8 text-muted-foreground opacity-50" />
              <p className="text-xs font-bold text-foreground">No Dealership Parts Ordered Yet</p>
              <p className="text-[11px] text-muted-foreground max-w-sm mx-auto">
                Procure verified OEM fitments, tuning accessories, and workshop spares directly through the platform.
              </p>
              <Button asChild size="sm" variant="outline" className="text-xs font-bold mt-1">
                <Link to={appRoutes.parts}>Browse Parts Catalog</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Summary */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 border-b border-border/60 pb-3">
            <Zap className="size-5 text-purple-400" />
            <h3 className="font-display text-base font-bold text-foreground">
              Dealership Entitlements &amp; Access
            </h3>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {permissionItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-background/60 px-4 py-3"
              >
                <span className="text-xs font-medium text-muted-foreground">{item.label}</span>
                <Badge variant={item.allowed ? 'success' : 'outline'} className="gap-1 text-[11px]">
                  {item.allowed ? <CheckCircle2 className="size-3" /> : <Lock className="size-3 text-muted-foreground" />}
                  {item.allowed ? 'Allowed' : 'Locked'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
